<i18n
  src="@/locales/views/customer/reporting/container/show_customer_report.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    name="customer_reporting_container_list_reports_path"
    :params="{
      customer_id: customer_id,
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
      container_id: container_id
    }"
  />

  <bread-crumbs
    :labels="[
      module_name,
      agro_exporter?.name,
      agro_exporter_process?.name,
      container?.container_code,
      customer_report?.name
    ]"
  ></bread-crumbs>

  <div v-if="load_data_general" class="mt-8 flex flex-col">
    <container-info
      class="bg-white rounded-md pb-6"
      :container="container"
      v-if="container"
    ></container-info>
    <div class="bg-white rounded-md flex">
      <div class="rounded-md px-6 flex-1">
        <div class="bg-white mt-8">
          <div v-if="customer_report.for_container">
            <apexchart
              v-if="
                seriesDataForContainer(report).length > 0 &&
                chartOptionsForContainer(report)
              "
              height="400"
              :options="chartOptionsForContainer(report)"
              :series="seriesDataForContainer(report)"
            ></apexchart>
          </div>
          <div v-if="customer_report.for_measurement_evolution" id="wrapper">
            <div id="chart-line" v-for="(chart, index) in report" :key="index">
              <apexchart
                height="200"
                :options="chart_options_for_evol[index]"
                :series="series_data_for_evol[index]"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import VueApexCharts from "vue3-apexcharts";

import Requester from "@/views/customer/requesters/reporting/container/show_customer_report.js";
import ContainerInfo from "@/components/views/customer/reporting/ContainerInfo.vue";

export default {
  components: {
    ContainerInfo,
    apexchart: VueApexCharts
  },
  name: "CustomerReportingContainerShowCustomerReport",
  props: [
    "customer_id",
    "agro_exporter_id",
    "agro_exporter_process_id",
    "container_id",
    "customer_report_id"
  ],
  data() {
    return {
      load_data_general: false,
      agro_exporter: {},
      agro_exporter_process: {},
      container: null,
      customer_report: {},
      report: null
    };
  },
  computed: {
    series_data_for_evol: function () {
      return this.report.map(x => {
        return [
          {
            name: x.label,
            data: [null, ...x.data, null]
          }
        ];
      });
    },
    chart_options_for_evol: function () {
      return this.report.map((x, i) => {
        return {
          chart: {
            id: i + 1,
            group: "social",
            type: x.graph_type
          },
          colors: [x.color],
          labels: ["", ...x.labels, ""],
          yaxis: {
            title: { text: x.label }
          }
        };
      });
    }
  },
  methods: {
    fetchData: async function() {
      await this.fetchAgroExporter();
      await this.fetchAgroExporterProcess();
      await this.fetchContainer();
      await this.fetchCustomerReport();
      await this.fetchReport();
      this.load_data_general = true;
    },
    fetchAgroExporter: async function () {
      let response = await Requester.fetchAgroExporter(
        this.customer_id,
        this.agro_exporter_id
      );
      this.agro_exporter = response.data;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.customer_id,
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer(
        this.customer_id,
        this.container_id
      );
      this.container = response.data;
    },
    fetchCustomerReport: async function () {
      let response = await Requester.fetchCustomerReport(
        this.customer_id,
        this.customer_report_id
      );
      this.customer_report = response.data;
    },
    fetchReport: async function () {
      let response = await Requester.fetchReport(
        this.customer_id,
        this.customer_report_id,
        this.container_id
      );
      this.report = response.data;
    },

    seriesDataForContainer(report) {
      if (report == null) return [];
      return report.series.map(serie => {
        return {
          name: serie.name,
          type: serie.graph_type,
          data: serie.data
        };
      });
    },
    chartOptionsForContainer(report) {
      if (report == null) return null;
      return {
        chart: {
          height: 350,
          stacked: report.stacked
        },
        legend: {
          position: "top"
        },
        stroke: {
          width: report.series.map(serie => serie.stroke)
        },
        dataLabels: {
          enabled: false
        },
        labels: report.xaxis.labels,
        yaxis: this.yAxis(report),
        colors: this.colors(report),
        annotations: {
          yaxis: this.yAnnotations(report)
        },
        xaxis: this.xAxis(report)
      };
    },
    colors(report) {
      if (report == null) return [];
      let colors = report.colors;
      return report.series.map((serie, i) => {
        return serie.color || colors[i];
      });
    },
    xAxis(report) {
      if (report == null) return {};
      return {
        title: {
          text: report.xaxis.title
        },
        labels: {
          rotate: -45,
          rotateAlways: true
        }
      };
    },
    yAxis(report) {
      if (report == null) return [];
      return report.series.map(serie => {
        let axis = {
          title: {
            text: serie.label
          },
          opposite: serie.yaxis.opposite,
          show: serie.yaxis.show,
          min: serie.yaxis.min,
          labels: {
            formatter: function (val) {
              if (val == null) return val;
              else if (Number.isSafeInteger(val)) return val.toFixed(0);
              else return val.toFixed(2);
            }
          }
        };
        if (serie.graph_type == "column") {
          axis["max"] = serie.yaxis.max || this.maxValue(report);
        } else if (serie.graph_type == "line") {
          axis["max"] = serie.yaxis.max || null;
        }
        if (serie.yaxis.steps_number) {
          axis["tickAmount"] = serie.yaxis.steps_number;
        }
        return axis;
      });
    },
    maxValue(report) {
      if (report == null) return 0;
      let serie_data = report.series
        .filter(serie => serie.graph_type == "column")
        .map(serie => serie.data);

      let max_value = 0;

      for (let index = 0; index < serie_data[0].length; index++) {
        let new_max = serie_data.reduce((acc, curr) => {
          return acc + curr[index];
        }, 0);
        if (new_max > max_value) max_value = new_max;
      }
      return max_value;
    },
    yAnnotations(report) {
      if (report == null) return [];
      return report.y_annotations.map(annotation => {
        return {
          y: annotation.y,
          strokeDashArray: 0,
          borderColor: annotation.color,
          yAxisIndex: annotation.serieIndex,
          label: {
            borderColor: annotation.color,
            style: {
              color: annotation.color
            },
            text: annotation.label,
            offsetY: 5,
            offsetX: 25
          }
        };
      });
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          customer_id: this.customer_id
        }
      }).then(() => {
        this.fetchData()
      })
    );
  }
};
</script>
